import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  Tags,
  MapPin,
  ClipboardList,
  FileText,
  Wrench,
  Package,
  TrendingUp,
  Building2,
  Car,
  ScrollText,
  Wallet,
  BarChart3,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Activity,
} from 'lucide-react';
import { useEquipmentStats } from '../hooks/useEquipment';
import { useWorkOrderStats } from '../hooks/useWorkOrders';
import { useArticleStats } from '../hooks/useArticles';
import { useVehicleStats, useExpiringDocuments } from '../hooks/useVehicles';
import { useContractStats } from '../hooks/useContracts';
import { useBudgetStats } from '../hooks/useBudgets';
import { useAuth } from '../hooks/useAuth';
import ContractExpiryAlerts from '../components/contracts/ContractExpiryAlerts';
import BudgetDashboard from '../components/budgets/BudgetDashboard';
import MaintenanceKPIsDashboard from '../components/dashboard/MaintenanceKPIsDashboard';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: equipmentStats, isLoading } = useEquipmentStats();
  const { data: workOrderStats } = useWorkOrderStats();
  const { data: articleStats } = useArticleStats();
  const { data: vehicleStats } = useVehicleStats();
  const { data: expiringDocs } = useExpiringDocuments(30);
  const { data: contractStats } = useContractStats();
  const { data: budgetStats } = useBudgetStats();

  const [showBudgetDetails, setShowBudgetDetails] = useState(false);
  const [showContractAlerts, setShowContractAlerts] = useState(false);
  const [showMaintenanceKPIs, setShowMaintenanceKPIs] = useState(true);

  // Check if there are critical alerts
  const hasBudgetAlerts = budgetStats && (budgetStats.budgetsExceeded > 0 || budgetStats.utilizationRate >= 80);
  const hasContractAlerts = contractStats && (contractStats.expired > 0 || contractStats.expiringIn30Days > 0);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboardPage.title')}</h1>
        <p className="mt-2 text-gray-600">
          {t('dashboardPage.welcome', { name: user?.full_name, role: user?.role })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/equipment"
          className="rounded-lg bg-white p-6 shadow transition hover:shadow-lg"
        >
          <h3 className="text-sm font-medium text-gray-500">{t('dashboardPage.stats.totalEquipment')}</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {isLoading ? '...' : equipmentStats?.total || 0}
          </p>
        </Link>

        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-sm font-medium text-gray-500">{t('dashboardPage.stats.operational')}</h3>
          <p className="mt-2 text-3xl font-semibold text-green-600">
            {isLoading ? '...' : equipmentStats?.byStatus.operational || 0}
          </p>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-sm font-medium text-gray-500">{t('dashboardPage.stats.inMaintenance')}</h3>
          <p className="mt-2 text-3xl font-semibold text-yellow-600">
            {isLoading ? '...' : equipmentStats?.byStatus.maintenance || 0}
          </p>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-sm font-medium text-gray-500">{t('dashboardPage.stats.critical')}</h3>
          <p className="mt-2 text-3xl font-semibold text-red-600">
            {isLoading ? '...' : equipmentStats?.byCriticality.critical || 0}
          </p>
        </div>
      </div>

      {/* Equipment Status Breakdown */}
      {equipmentStats && (
        <div className="mb-8 rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {t('dashboardPage.equipmentStatus.title')}
          </h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <div>
              <div className="text-sm text-gray-500">{t('dashboardPage.equipmentStatus.operational')}</div>
              <div className="mt-1 text-xl font-semibold text-green-600">
                {equipmentStats.byStatus.operational || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">{t('dashboardPage.equipmentStatus.maintenance')}</div>
              <div className="mt-1 text-xl font-semibold text-yellow-600">
                {equipmentStats.byStatus.maintenance || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">{t('dashboardPage.equipmentStatus.outOfService')}</div>
              <div className="mt-1 text-xl font-semibold text-red-600">
                {equipmentStats.byStatus.out_of_service || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">{t('dashboardPage.equipmentStatus.reserved')}</div>
              <div className="mt-1 text-xl font-semibold text-blue-600">
                {equipmentStats.byStatus.reserved || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">{t('dashboardPage.equipmentStatus.retired')}</div>
              <div className="mt-1 text-xl font-semibold text-gray-600">
                {equipmentStats.byStatus.retired || 0}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance KPIs */}
      <div className="mb-8 rounded-lg bg-white p-6 shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity className="mr-2 h-5 w-5 text-blue-500" />
            {t('dashboardPage.maintenanceKPIs.title', 'KPIs de Maintenance')}
          </h2>
          <button
            onClick={() => setShowMaintenanceKPIs(!showMaintenanceKPIs)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            {showMaintenanceKPIs ? t('common.hide', 'Masquer') : t('common.show', 'Afficher')}
            {showMaintenanceKPIs ? (
              <ChevronUp className="ml-1 h-4 w-4" />
            ) : (
              <ChevronDown className="ml-1 h-4 w-4" />
            )}
          </button>
        </div>
        {showMaintenanceKPIs && <MaintenanceKPIsDashboard />}
      </div>

      {/* Stock Stats */}
      {articleStats && (
        <div className="mb-8 rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {t('dashboardPage.stock.title')}
          </h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Link to="/articles" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.stock.totalArticles')}</div>
              <div className="mt-1 text-xl font-semibold text-blue-600">
                {articleStats.total}
              </div>
            </Link>
            <Link to="/articles?low_stock=true" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.stock.lowStock')}</div>
              <div className="mt-1 text-xl font-semibold text-orange-600">
                {articleStats.lowStockCount}
              </div>
            </Link>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.stock.spareParts')}</div>
              <div className="mt-1 text-xl font-semibold text-purple-600">
                {articleStats.byCategory.spare_part || 0}
              </div>
            </div>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.stock.totalValue')}</div>
              <div className="mt-1 text-xl font-semibold text-green-600">
                {articleStats.totalValue.toFixed(0)} {t('common.currency')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Work Orders Stats */}
      {workOrderStats && (
        <div className="mb-8 rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {t('dashboardPage.workOrders.title')}
          </h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Link to="/work-orders?status=in_progress" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.workOrders.inProgress')}</div>
              <div className="mt-1 text-xl font-semibold text-purple-600">
                {workOrderStats.byStatus.in_progress || 0}
              </div>
            </Link>
            <Link to="/work-orders?status=scheduled" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.workOrders.scheduled')}</div>
              <div className="mt-1 text-xl font-semibold text-blue-600">
                {workOrderStats.byStatus.scheduled || 0}
              </div>
            </Link>
            <Link to="/work-orders?status=pending_approval" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.workOrders.pending')}</div>
              <div className="mt-1 text-xl font-semibold text-yellow-600">
                {workOrderStats.byStatus.pending_approval || 0}
              </div>
            </Link>
            <Link to="/work-orders?priority=critical" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.workOrders.critical')}</div>
              <div className="mt-1 text-xl font-semibold text-red-600">
                {workOrderStats.byPriority.critical || 0}
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Vehicles Stats */}
      {vehicleStats && (
        <div className="mb-8 rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {t('dashboardPage.vehicles.title')}
          </h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Link to="/vehicles" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.vehicles.totalVehicles')}</div>
              <div className="mt-1 text-xl font-semibold text-blue-600">
                {vehicleStats.total}
              </div>
            </Link>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.vehicles.assigned')}</div>
              <div className="mt-1 text-xl font-semibold text-green-600">
                {vehicleStats.assigned}
              </div>
            </div>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.vehicles.unassigned')}</div>
              <div className="mt-1 text-xl font-semibold text-gray-600">
                {vehicleStats.unassigned}
              </div>
            </div>
            <Link to="/vehicles" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.vehicles.insuranceExpiring')}</div>
              <div className="mt-1 text-xl font-semibold text-orange-600">
                {vehicleStats.insuranceExpiringSoon}
              </div>
            </Link>
            <Link to="/vehicles" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.vehicles.technicalExpiring')}</div>
              <div className="mt-1 text-xl font-semibold text-orange-600">
                {vehicleStats.technicalExpiringSoon}
              </div>
            </Link>
          </div>
          {expiringDocs && (expiringDocs.insurance.length > 0 || expiringDocs.technical.length > 0) && (
            <div className="mt-4 rounded-md bg-orange-50 p-4">
              <div className="flex">
                <span className="text-xl">⚠️</span>
                <p className="ml-2 text-sm text-orange-800">
                  {t('dashboardPage.vehicles.expiringDocuments', {
                    count: expiringDocs.insurance.length + expiringDocs.technical.length + expiringDocs.registration.length
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contracts Stats */}
      {contractStats && (
        <div className="mb-8 rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              {t('dashboardPage.contracts.title')}
              {hasContractAlerts && (
                <AlertTriangle className="ml-2 h-5 w-5 text-orange-500" />
              )}
            </h2>
            <button
              onClick={() => setShowContractAlerts(!showContractAlerts)}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              {showContractAlerts ? t('dashboardPage.contracts.hideAlerts') : t('dashboardPage.contracts.showAlerts')}
              {showContractAlerts ? (
                <ChevronUp className="ml-1 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-1 h-4 w-4" />
              )}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Link to="/contracts" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.contracts.totalContracts')}</div>
              <div className="mt-1 text-xl font-semibold text-blue-600">
                {contractStats.total}
              </div>
            </Link>
            <Link to="/contracts?status=active" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.contracts.active')}</div>
              <div className="mt-1 text-xl font-semibold text-green-600">
                {contractStats.byStatus.active || 0}
              </div>
            </Link>
            <Link to="/contracts?status=expired" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.contracts.expired')}</div>
              <div className="mt-1 text-xl font-semibold text-red-600">
                {contractStats.expired || 0}
              </div>
            </Link>
            <Link to="/contracts?expiring=30" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.contracts.expiringSoon')}</div>
              <div className="mt-1 text-xl font-semibold text-orange-600">
                {contractStats.expiringIn30Days}
              </div>
            </Link>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.contracts.totalValue')}</div>
              <div className="mt-1 text-xl font-semibold text-purple-600">
                {(contractStats.totalValue / 1000).toFixed(0)}k {t('common.currency')}
              </div>
            </div>
          </div>
          {(contractStats.expired > 0 || contractStats.expiringIn30Days > 0) && (
            <div className="mt-4 rounded-md bg-orange-50 p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                <p className="ml-2 text-sm text-orange-800">
                  {contractStats.expired > 0 && t('dashboardPage.contracts.expiredContracts', { count: contractStats.expired })}
                  {contractStats.expired > 0 && contractStats.expiringIn30Days > 0 && ' ' + t('common.and') + ' '}
                  {contractStats.expiringIn30Days > 0 && t('dashboardPage.contracts.expiringIn30Days', { count: contractStats.expiringIn30Days })}
                </p>
              </div>
            </div>
          )}
          {showContractAlerts && (
            <div className="mt-6 border-t pt-6">
              <ContractExpiryAlerts maxItems={5} />
            </div>
          )}
        </div>
      )}

      {/* Budgets Stats */}
      {budgetStats && (
        <div className="mb-8 rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              {t('dashboardPage.budgets.title')}
              {hasBudgetAlerts && (
                <AlertTriangle className="ml-2 h-5 w-5 text-orange-500" />
              )}
            </h2>
            <button
              onClick={() => setShowBudgetDetails(!showBudgetDetails)}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              {showBudgetDetails ? t('dashboardPage.budgets.hideDetails') : t('dashboardPage.budgets.showDetails')}
              {showBudgetDetails ? (
                <ChevronUp className="ml-1 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-1 h-4 w-4" />
              )}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Link to="/budgets" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.budgets.totalBudgets')}</div>
              <div className="mt-1 text-xl font-semibold text-blue-600">
                {budgetStats.total}
              </div>
            </Link>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.budgets.allocated')}</div>
              <div className="mt-1 text-xl font-semibold text-green-600">
                {(budgetStats.totalAllocated / 1000).toFixed(0)}k {t('common.currency')}
              </div>
            </div>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.budgets.spent')}</div>
              <div className="mt-1 text-xl font-semibold text-purple-600">
                {(budgetStats.totalSpent / 1000).toFixed(0)}k {t('common.currency')}
              </div>
            </div>
            <div className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.budgets.available')}</div>
              <div className="mt-1 text-xl font-semibold text-cyan-600">
                {(budgetStats.totalAvailable / 1000).toFixed(0)}k {t('common.currency')}
              </div>
            </div>
            <Link to="/budgets" className="hover:bg-gray-50 rounded p-2">
              <div className="text-sm text-gray-500">{t('dashboardPage.budgets.utilizationRate')}</div>
              <div className={`mt-1 text-xl font-semibold ${budgetStats.utilizationRate >= 80 ? 'text-red-600' : budgetStats.utilizationRate >= 60 ? 'text-orange-600' : 'text-green-600'}`}>
                {budgetStats.utilizationRate.toFixed(0)}%
              </div>
            </Link>
          </div>
          {budgetStats.budgetsExceeded > 0 && (
            <div className="mt-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <p className="ml-2 text-sm text-red-800">
                  {t('dashboardPage.budgets.exceeded', { count: budgetStats.budgetsExceeded })}
                </p>
              </div>
            </div>
          )}
          {showBudgetDetails && (
            <div className="mt-6 border-t pt-6">
              <BudgetDashboard />
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('dashboardPage.quickActions.title')}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <Link
            to="/equipment"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <Settings className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.equipment')}
            </div>
          </Link>
          <Link
            to="/categories"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <Tags className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.categories')}
            </div>
          </Link>
          <Link
            to="/locations"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <MapPin className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.locations')}
            </div>
          </Link>
          <Link
            to="/work-orders"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <ClipboardList className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.workOrders')}
            </div>
          </Link>
          <Link
            to="/work-requests"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <FileText className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.requests')}
            </div>
          </Link>
          <Link
            to="/preventive-plans"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <Wrench className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.preventivePlans')}
            </div>
          </Link>
          <Link
            to="/articles"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <Package className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.stock')}
            </div>
          </Link>
          <Link
            to="/stock-movements"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <TrendingUp className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.movements')}
            </div>
          </Link>
          <Link
            to="/suppliers"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <Building2 className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.suppliers')}
            </div>
          </Link>
          <Link
            to="/vehicles"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <Car className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.vehicles')}
            </div>
          </Link>
          <Link
            to="/contracts"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <ScrollText className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.contracts')}
            </div>
          </Link>
          <Link
            to="/budgets"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <Wallet className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboardPage.quickActions.budgets')}
            </div>
          </Link>
          <Link
            to="/reports"
            className="rounded-lg border border-gray-200 p-4 text-center transition hover:border-blue-500 hover:shadow"
          >
            <BarChart3 className="mx-auto h-8 w-8 text-gray-600" />
            <div className="mt-2 text-sm font-medium text-gray-900">{t('dashboardPage.quickActions.reports')}</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
